import React from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import { Button, Checkbox, createStyles, FormControl, FormControlLabel, FormLabel, Grid,
    Icon, IconButton, InputAdornment, makeStyles, TextField, Typography} from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';

import { ClipperContext } from '../ClipperContext';
import { HomeContext } from '../../Home/HomeContext';
import { AuthContext } from '../../Authentication/AuthContext';
import { ClipperModel, clipperDataInitial } from '../../../Models/ClipperModel';
import ClipperService from '../../../Services/ClipperService';
import { BasicConfirmDialog } from '../../../shared/components/BasicConfirmDialog';
import { BasicPage } from '../../../shared/components/BasicPage';
import { PageLocation } from '../../../shared/enum/PageLocation';
import { theme } from '../../../theme';

const useStyles = makeStyles(() => createStyles({
    linkColor: {
        color: '#2C64D0',
        textOverflow: 'ellipsis',
        '&:hover': {
            textDecoration: 'underline',
            cursor: 'pointer',
        },
    },
    editText: {
        textOverflow: 'ellipsis',
    },
    copyClipper: {
        marginTop: theme.spacing(3)
    }
}));

export function ClipperInput() {
    const classes = useStyles();

    const homeContext = React.useContext(HomeContext);
    const authContext = React.useContext(AuthContext);
    const clipperContext = React.useContext(ClipperContext);

    const [clipper, setClipper] = React.useState<ClipperModel>(clipperDataInitial);
    const [clippingUrlDisabled, setClippingUrlDisabled] = React.useState<boolean>(false);
    const [clippingUrlFocused, setClippingUrlFocused] = React.useState<boolean>(false);
    const [editMode, setEditMode] = React.useState<boolean>(false);
    const [confirmEditDialogOpen, setConfirmEditDialogOpen] = React.useState<boolean>(false);

    const [clipperNameError, setClipperNameError] = React.useState<boolean>(false);
    const [clipperColumnNewsTitleError, setClipperColumnNewsTitleError] = React.useState<boolean>(false);
    const [clipperColumnClipperLinkError, setClipperColumnClipperLinkError] = React.useState<boolean>(false);
    const [clipperColumnPublishDateError, setClipperColumnPublishDateError] = React.useState<boolean>(false);
    const [clipperColumnPublicationPlaceError, setClipperColumnPublicationPlaceError] = React.useState<boolean>(false);
    const [clipperColumnMediaError, setClipperColumnMediaError] = React.useState<boolean>(false);
    const [clipperLineStartError, setClipperLineStartError] = React.useState<boolean>(false);

    const [clipperSelectedError, setClipperSelectedError] = React.useState<boolean>(false);
    const [clipperCopySettings, setClipperCopySettings] = React.useState<boolean>(false);
    const [clipperSelected, setClipperSelected] = React.useState<ClipperModel | null>(null);
    const [clipperList, setClipperList] = React.useState<ClipperModel[]>([]);
    const [confirmCopyClipper, setConfirmCopyClipper] = React.useState<boolean>(true);
    const [openConfirmCopyClipper, setOpenConfirmCopyClipper] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (homeContext.pageLocation === PageLocation.CLIPPER_EDIT) {
            const currentClipper = JSON.parse(JSON.stringify(clipperContext.clipper));
            setConfirmCopyClipper(!(currentClipper.storageSettingsExists && currentClipper.mediaDictionarySettingsExists));
            setClipper(currentClipper);
            setEditMode(true);
            fetchClipperCopy();
        } else if (homeContext.pageLocation === PageLocation.CLIPPER_INPUT) {
            setEditMode(false);
            setClipper(clipperDataInitial);
            fetchClipperCopy();
        }
    }, [homeContext.pageLocation]);

    React.useEffect(()=>{
        if (clipperSelected != null)
            if (!clipperSelected.storageSettingsExists || !clipperSelected.mediaDictionarySettingsExists)
            {
                homeContext.showWarning('Esta clipadora não possui configuração de armazenamento de notícia e/ou não possui configuração de dicionário de mídia.'
                    +' Para copiar as configurações escolha outra clipadora.');

                setClipperSelected(null);
            }
    },[clipperSelected]);

    const hasError = () => {
        const errorClipperName: boolean = !clipper.name || clipper.name.length == 0;
        const errorColumnNewsTitle: boolean = !clipper.columnTitle || clipper.columnTitle.length == 0;
        const errorColumnClipperLink: boolean = !clipper.columnClipperLink || clipper.columnClipperLink.length == 0;
        const errorColumnPublishDate: boolean = !clipper.columnPublishDate || clipper.columnPublishDate.length == 0;
        const errorColumnPublicationPlace: boolean = !clipper.columnPublicationPlace || clipper.columnPublicationPlace.length == 0;
        const errorColumnMedia: boolean = !clipper.columnMedia || clipper.columnMedia.length == 0;
        const errorLineStart: boolean = !clipper.lineStart || clipper.lineStart < 1;
        const errorClipperSelected = clipperCopySettings && (clipperSelected == null || !clipperSelected.mediaDictionarySettingsExists || !clipperSelected.storageSettingsExists);

        setClipperNameError(errorClipperName);
        setClipperColumnNewsTitleError(errorColumnNewsTitle);
        setClipperColumnClipperLinkError(errorColumnClipperLink);
        setClipperColumnPublishDateError(errorColumnPublishDate);
        setClipperColumnPublicationPlaceError(errorColumnPublicationPlace);
        setClipperColumnMediaError(errorColumnMedia);
        setClipperLineStartError(errorLineStart);
        setClipperSelectedError(errorClipperSelected);
   
        return errorClipperName || errorColumnNewsTitle || errorColumnClipperLink || errorColumnPublishDate ||
            errorColumnPublicationPlace || errorColumnMedia || errorLineStart || errorClipperSelected;
    };

    const handleSave = () => {
        if (!hasError()) {
            homeContext.setOpenLoadingDialog(true);

            if (editMode) {
                ClipperService.clipperService()
                    .update(clipper)
                    .then(() => {
                        homeContext.showSuccess('Clipadora alterada com sucesso!');
                        setClipper(clipperDataInitial);
                        homeContext.setPageLocation(PageLocation.CLIPPER_SEARCH);
                    })
                    .catch((error: AxiosError) => homeContext.showError('Ops... Ocorreu um erro e não foi possível alterar a nova clipadora. <br/>', error))
                    .finally(() => homeContext.setOpenLoadingDialog(false));
            } else {
                clipper.userId = authContext.login.user.id;

                ClipperService.clipperService()
                    .create(clipper)
                    .then(() => {
                        setClipper(clipperDataInitial);
                        homeContext.showSuccess('Clipadora salva com sucesso!');
                    })
                    .catch((error: AxiosError) => homeContext.showError('Ops... Ocorreu um erro e não foi possível salvar a nova clipadora. <br/>', error))
                    .finally(() => homeContext.setOpenLoadingDialog(false));
            }
            setClipperCopySettings(false);
        }
    };

    const getClipperName = () => {
        return (
            <Grid item xs={12} sm={editMode ? 5 : 6}>
                <TextField
                    label="Nome da clipadora"
                    variant="outlined"
                    fullWidth
                    value={clipper.name}
                    onChange={ (event) => setClipper({ ...clipper, name: event.target.value }) }
                    error={clipperNameError && !clipper.name}
                    inputProps={{ maxLength: 200 }}
                    helperText={clipperNameError && !clipper.name ? 'Campo obrigatório' : ''} />
            </Grid>
        );
    };

    const getClipperUrl = () => {
        return (
            <Grid item xs={12} sm={editMode ? 5 : 6}>
                <TextField
                    label="URL de busca de clipping"
                    variant="outlined"
                    fullWidth
                    value={clipper.clippingUrl}
                    onBlur={() => {
                        if (clipper.clippingUrl.length > 0) {
                            setClippingUrlDisabled(true);
                            setClippingUrlFocused(false);
                        }
                        else {
                            setClippingUrlDisabled(false);
                            setClippingUrlFocused(false);
                        }
                    }}
                    onChange={(event) => setClipper({ ...clipper, clippingUrl: event.target.value }) }
                    inputRef={input => clippingUrlFocused && input && input.focus()}
                    inputProps={{
                        onClick: () => { (clippingUrlDisabled && clipper.clippingUrl) && window.open(clipper.clippingUrl, '_blank'); }
                    }}
                    InputProps={{
                        readOnly: clippingUrlDisabled,
                        classes: { input: (clippingUrlDisabled ? classes.linkColor : classes.editText) },
                        endAdornment: (clippingUrlDisabled && (clipper.clippingUrl.length > 0) &&
                            <InputAdornment position="end">
                                <IconButton onClick={() => { setClippingUrlDisabled(false); setClippingUrlFocused(true); }}>
                                    <Icon>edit</Icon>
                                </IconButton>
                            </InputAdornment>)
                    }} />
            </Grid>
        );
    };

    const getStatus = () => {
        return (
            <Grid item xs={12} sm={2}>
                <FormControl component="fieldset">
                    <FormLabel component="legend">Status</FormLabel>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={clipper.active}
                                onChange={() => setClipper({ ...clipper, active: !clipper.active })}
                                name="Ativo"
                                color="primary" />
                        }
                        label="Ativo" />
                </FormControl>
            </Grid>
        );
    };

    const getSpreadsheetInformationTitle = () => {
        return (
            <Grid item>
                <Grid
                    container
                    direction="column"
                    spacing={1}>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1">
                            Informações da planilha
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="body2">
                            {'Para preenchimento automatizado, insira nos campos a coluna onde se encontra cada dado indicado. ' + 
                            'Deixe em branco os campos que não existem na planilha'}
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>
        );
    };

    const getSpreadsheetColumnField = (label: string, value: string, prop: keyof ClipperModel, required: boolean, error: boolean) => {
        return (
            <Grid item xs={4}>
                <TextField
                    label={label}
                    variant="outlined"
                    fullWidth
                    required={required}
                    value={value}
                    onChange={(event) =>
                        setClipper({ ...clipper, [prop]: event.target.value.replace(/[^a-zA-z]/ig, '').toUpperCase() })
                    }
                    error={error && value.length == 0}
                    inputProps={{
                        maxLength: 2,
                        style: { textTransform: 'uppercase' }
                    }}
                    helperText={error && value.length == 0 ? 'Campo obrigatório' : ''} />
            </Grid>
        );
    };

    const getSpreadsheetLineOfStart = () => {
        return (
            <Grid item xs={4}>
                <TextField
                    label="Linha de início das informações"
                    variant="outlined"
                    fullWidth
                    required
                    type="number"
                    value={clipper.lineStart ? clipper.lineStart : ''}
                    onChange={(event) =>
                        setClipper({ ...clipper, lineStart: parseInt(event.target.value) })
                    }
                    onInput = {(e: any) => {
                        e.target.value = Math.max(0, parseInt(e.target.value) ).toString().slice(0,9);
                    }}
                    error={clipperLineStartError && (clipper.lineStart == null || clipper.lineStart < 1)}
                    helperText={clipperLineStartError
                        ? (clipper.lineStart == null ? 'Campo obrigatório' : (clipper.lineStart < 1) && 'Valor inválido')
                        : ''} />
            </Grid>
        );
    };

    const getSpreadsheetInformationFields = () => {
        return (
            <>
                {getSpreadsheetLineOfStart()}
                {getSpreadsheetColumnField('ID da matéria', clipper.columnIdNews, 'columnIdNews', false, false)}
                {getSpreadsheetColumnField('Título da matéria', clipper.columnTitle, 'columnTitle', true, clipperColumnNewsTitleError)}
                {getSpreadsheetColumnField('Link da notícia na clipadora', clipper.columnClipperLink, 'columnClipperLink', true, clipperColumnClipperLinkError)}
                {getSpreadsheetColumnField('Link da matéria original', clipper.columnNewsLink, 'columnNewsLink', false, false)}
                {getSpreadsheetColumnField('Data de inserção', clipper.columnPublishDate, 'columnPublishDate', true, clipperColumnPublishDateError)}
                {getSpreadsheetColumnField('Jornalista', clipper.columnJournalist, 'columnJournalist', false, false)}
                {getSpreadsheetColumnField('Veículo', clipper.columnPublicationPlace, 'columnPublicationPlace', true, clipperColumnPublicationPlaceError)}
                {getSpreadsheetColumnField('Mídia', clipper.columnMedia, 'columnMedia', true, clipperColumnMediaError)}
                {getSpreadsheetColumnField('Centimetragem', clipper.columnCentimeter, 'columnCentimeter', false, false)}
            </>
        );
    };
    
    const updateClipperStorageSettings = () => {
        if (clipperSelected?.storageSettingsExists && clipperSelected?.mediaDictionarySettingsExists){
            clipper.clipperIdCopy = clipperSelected.id;
            clipper.copySettings = true;
        }
    };

    const getSaveButton = () => {
        return (
            <Grid item xs={12}>
                <Grid
                    container
                    spacing={1}
                    direction="row"
                    justify="flex-end"
                    alignItems="flex-end">
                    <Grid item>
                        <Button
                            onClick={() => { updateClipperStorageSettings();  (editMode ? (!hasError() && setConfirmEditDialogOpen(true)) : handleSave());}}
                            variant='contained'
                            color='primary'
                            size='large'>
                            {editMode ? 'SALVAR ALTERAÇÕES' : 'SALVAR CLIPADORA'}
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        );
    };

    const getConfirmEditDialog = () => {
        return (
            <BasicConfirmDialog
                title="Confirmar as alterações desta clipadora?"
                description="Ao confirmar, as alterações serão aplicadas."
                handleFirstButton={() => setConfirmEditDialogOpen(false)}
                handleSecondButton={() => { handleSave(); setConfirmEditDialogOpen(false); }}
                textFirstButton="Cancelar"
                textSecondButton="Confirmar"
                open={confirmEditDialogOpen} />
        );
    };

    const getCopyClipperSettings = () => {
        return (
            <Grid item xs={12}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={clipperCopySettings}
                            onChange={ (_,checked) => {
                                setClipperCopySettings(checked);
                                if (checked && (clipper.storageSettingsExists || clipper.mediaDictionarySettingsExists))
                                {
                                    setConfirmCopyClipper(false);
                                    setOpenConfirmCopyClipper(true);
                                } else {
                                    setClipperSelected(null);
                                }
                            }}
                            name="Copiar configuração de armazenamento e dicionário de mídias."
                            color="primary"
                        />
                    }
                    label = "Copiar configuração de armazenamento e dicionário de mídias."
                />
            </Grid>
        );
    };
    const fetchClipperCopy = () => {
        ClipperService.clipperService().getAll()
            .then((res: AxiosResponse) => {
                let list = res.data;
                list = list.filter((c : ClipperModel) => c.id != clipperContext.clipper.id);
                setClipperList(list);
            })
            .catch((error: AxiosError) => { homeContext.showError('', error); });
    };

    const getSelectClipperSettings = () => {
        return(
            <Grid item>
                <Grid container direction="column" spacing={1}>
                    <Grid item xs={12}>
                        <Typography variant="body2">
                        Escolha uma planilha para copiar as configurações de armazenamento e dicionário de mídias.
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid className = {classes.copyClipper}>
                            { getClippers() }
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        ); 
    };

    const handleDiscardCopy = () => {
        setClipperCopySettings(!clipperCopySettings);
        setOpenConfirmCopyClipper(false);
    };

    const handleConfirmCopy = () => {
        setConfirmCopyClipper(true);
        setOpenConfirmCopyClipper(false);
    };

    const getConfirmCopyClipperDialog = () => {
        return (
            <BasicConfirmDialog
                title="Essa clipadora já possui configuração..."
                description={'Ao copiar as configurações de armazenamento de notícia e de dicionário de mídia de outra clipadora,'
                    + 'as configurações atuais serão substituídas pelas novas após o salvamento.'}
                handleFirstButton={ handleDiscardCopy }
                handleSecondButton={ handleConfirmCopy }
                textFirstButton="Cancelar"
                textSecondButton="Confirmar"
                open={ openConfirmCopyClipper } />
        );
    };

    const getClippers = () => {
        return (
            <Grid item xs={12} sm={9}>
                <Autocomplete
                    id="clipper"
                    value={clipperSelected}
                    onChange={(event, value: ClipperModel | null) => setClipperSelected(value)}  
                    options={clipperList}
                    getOptionLabel={(option: ClipperModel) => option.name}
                    fullWidth
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    renderInput={(params) => <TextField {...params}
                        label="Clipadora"
                        variant="outlined"
                        error={clipperSelectedError && (!clipperSelected?.mediaDictionarySettingsExists || !clipperSelected?.storageSettingsExists)}
                        helperText={clipperSelectedError && (!clipperSelected?.mediaDictionarySettingsExists || !clipperSelected?.storageSettingsExists) ? 
                            'Selecione uma clipadora que possua configuração de armazenamento de notícia e de dicionário de mídia' : ''}
                    />}
                />
            </Grid>
        );
    };

    const getWithoutClipperSettings = () => {
        return (
            <Grid item xs={12}>
                <Alert severity="warning" icon={<Icon className={'material-icons-round'}>warning_amber</Icon>}>
                    {'Esta clipadora não possui configuração de armazenamento de notícia e não possui configuração de dicionário de mídia. ' +  
                    'Utilize a opção abaixo para copiar a configuração de armazenamento de outra clipadora/modelo.'}
                </Alert>
            </Grid>
        );
    };

    return(
        <BasicPage
            title={editMode
                ? 'Edição de clipadora'
                : 'Cadastro de clipadora'}
            subtitle={editMode
                ? 'Edite os campos que deseja alterar para o usuário.'
                : 'Para cadastrar uma nova clipadora, insira os dados solicitados abaixo.'}>
            <>
                { getClipperName() }
                { getClipperUrl() }
                { editMode && getStatus() }
                { getSpreadsheetInformationTitle() }
                { getSpreadsheetInformationFields() }
                { (!clipper.storageSettingsExists || !clipper.mediaDictionarySettingsExists) && getWithoutClipperSettings() }
                { getCopyClipperSettings() }
                { clipperCopySettings && (clipper.storageSettingsExists || clipper.mediaDictionarySettingsExists) && getConfirmCopyClipperDialog() }
                { confirmCopyClipper && clipperCopySettings && getSelectClipperSettings()}
                { getSaveButton() }
                { editMode && getConfirmEditDialog() }
            </>
        </BasicPage>
    );
}
